import { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import MetaTags from '@/components/MetaTags'
import ProductCustomizationInline from '@/components/ProductCustomizationInline'
import { Check, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'
import api from '@/services/api'

type Orientation = 'landscape' | 'portrait' | 'square'

interface ProductImage {
  type: 'product_image'
  id: string
  attributes: {
    image_url: string
  }
}

interface DynamicProductImage {
  type: 'dynamic_product_images'
  id: string
  attributes: {
    image_url: string
  }
}

interface RawItem {
  id: string
  attributes: {
    name: string
    description: string
    sku?: string
    thumbnail_url?: string
    level?: string
  }
  relationships?: {
    dynamic_products?: {
      data: Array<{ type: string; id: string }>
    }
    product_images?: {
      data: Array<{ type: string; id: string }>
    }
  }
}

interface DynamicProduct {
  type: 'dynamic_products'
  id: string
  attributes: {
    width: number
    height: number
    value: string
    orientation?: Orientation
  }
  relationships?: {
    dynamic_product_images?: {
      data: Array<{ type: string; id: string }>
    }
  }
}

interface DynamicLine {
  type: 'dynamic_lines'
  id: string
  attributes: {
    price: string
    number_pages: number
    maximum_photos: number
    dynamic_product_id: string
  }
}

type IncludedItem =
  | DynamicProduct
  | DynamicLine
  | ProductImage
  | DynamicProductImage

function preloadImages(urls: string[]) {
  urls.forEach(url => {
    const img = new Image()
    img.src = url
  })
}

interface Product {
  id: string
  name: string
  description: string
  features: string[]
  level: string
  basePrice: number
  image: string
  images: string[]
}

const ProductDetails = () => {
  const { id } = useParams<{ id: string }>()
  const [product, setProduct] = useState<Product | null>(null)
  const [dynamicProductImagesMap, setDynamicProductImagesMap] = useState<
    Record<string, string[]>
  >({})
  const [selectedFormatId, setSelectedFormatId] = useState<string | null>(
    null,
  )
  const [isLoading, setIsLoading] = useState(true)
  const [selectedImage, setSelectedImage] = useState(0)

  useEffect(() => {
    if (!id) return
    fetchProduct()
  }, [id])

  const handleFormatChange = useCallback((formatId: string) => {
    setSelectedFormatId(formatId)
    setSelectedImage(0)
  }, [])

  async function fetchProduct() {
    const companyId = import.meta.env.VITE_COMPANY_ID ?? ''
    setIsLoading(true)

    try {
      const res = await api.get<{
        data: RawItem
        included?: IncludedItem[]
      }>(`/b2c/public/products/${id}`, {
        headers: { 'X-Current-Company-ID': companyId },
        params: {
          include:
            'dynamic_products,dynamic_products.dynamic_lines,dynamic_products.dynamic_product_images,product_images',
        },
      })

      const attr = res.data.data.attributes
      const included = res.data.included || []

      const dynamicProductIds =
        res.data.data.relationships?.dynamic_products?.data.map(dp => dp.id) ||
        []

      const relatedLines = included.filter(
        (inc): inc is DynamicLine =>
          inc.type === 'dynamic_lines' &&
          'dynamic_product_id' in inc.attributes &&
          dynamicProductIds.includes(
            (inc.attributes as { dynamic_product_id: string })
              .dynamic_product_id,
          ),
      )

      let minPrice = 0
      if (relatedLines.length > 0) {
        const prices = relatedLines.map(line => Number(line.attributes.price))
        minPrice = Math.min(...prices)
      }

      const descriptionLines = attr.description
        .split('\n')
        .map(line => line.trim())
        .filter(line => line)

      const shortDescription = descriptionLines[0] || attr.description
      const features = descriptionLines.slice(1)

      let level = 'Essencial'
      const nameLower = attr.name.toLowerCase()
      if (nameLower.includes('magnifique') || minPrice > 1000) {
        level = 'Luxo'
      } else if (
        nameLower.includes('virtuose') ||
        (minPrice > 600 && minPrice < 1000)
      ) {
        level = 'Premium'
      } else if (
        nameLower.includes('inspiração') ||
        nameLower.includes('inspiracao') ||
        (minPrice > 400 && minPrice <= 600)
      ) {
        level = 'Sofisticado'
      }

      const productImageIds =
        res.data.data.relationships?.product_images?.data.map(img => img.id) ||
        []

      const galleryImages = included
        .filter(
          (inc): inc is ProductImage =>
            inc.type === 'product_image' && productImageIds.includes(inc.id),
        )
        .map(img => img.attributes.image_url)

      const finalImages =
        galleryImages.length > 0
          ? galleryImages
          : attr.thumbnail_url
            ? [attr.thumbnail_url]
            : []

      const dynamicProducts = included.filter(
        (inc): inc is DynamicProduct => inc.type === 'dynamic_products',
      )
      const dynamicProductImageItems = included.filter(
        (inc): inc is DynamicProductImage =>
          inc.type === 'dynamic_product_images',
      )
      const imageById = Object.fromEntries(
        dynamicProductImageItems.map(img => [img.id, img.attributes.image_url]),
      )
      const map: Record<string, string[]> = {}
      for (const dp of dynamicProducts) {
        const ids =
          dp.relationships?.dynamic_product_images?.data?.map(d => d.id) ?? []
        map[dp.id] = ids
          .map(id => imageById[id])
          .filter((url): url is string => !!url)
      }
      setDynamicProductImagesMap(map)

      const allDynamicUrls = Object.values(map).flat()
      const urlsToPreload = [...finalImages, ...allDynamicUrls]
      if (urlsToPreload.length > 0) {
        preloadImages(urlsToPreload)
      }

      setProduct({
        id: res.data.data.id,
        name: attr.name,
        description: shortDescription,
        features: features.slice(0, 8),
        level: attr.level || level,
        basePrice: minPrice,
        image: attr.thumbnail_url || finalImages[0] || '',
        images: finalImages,
      })
    } catch (error) {
      console.error('Erro ao buscar produto:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const displayImages =
    product && selectedFormatId && dynamicProductImagesMap[selectedFormatId]?.length > 0
      ? dynamicProductImagesMap[selectedFormatId]
      : product?.images ?? []

  const nextImage = () => {
    if (displayImages.length > 0) {
      setSelectedImage(prev => (prev + 1) % displayImages.length)
    }
  }

  const prevImage = () => {
    if (displayImages.length > 0) {
      setSelectedImage(
        prev => (prev - 1 + displayImages.length) % displayImages.length,
      )
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </main>
        <Footer />
      </div>
    )
  }

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-2">Produto não encontrado</h2>
            <p className="text-muted-foreground">
              O produto que você procura não está disponível.
            </p>
          </div>
        </main>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <MetaTags
        title={`${product.name} - ${product.level} | FotoLivro Store`}
        description={product.description}
        keywords={`fotolivro ${product.name.toLowerCase()}, álbum fotográfico ${product.level.toLowerCase()}`}
      />
      <Header />

      <main className="flex-1">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
            <a href="/" className="hover:text-primary transition-colors">
              Home
            </a>
            <span>/</span>
            <a
              href="/#produtos"
              className="hover:text-primary transition-colors"
            >
              Produtos
            </a>
            <span>/</span>
            <span className="text-foreground font-medium">{product.name}</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mt-8 transition-all duration-700 opacity-100 translate-y-0">
            <div>
              <div className="space-y-4">
                {displayImages.length > 0 ? (
                  <>
                    <div className="relative aspect-square overflow-hidden rounded-lg bg-muted">
                      <img
                        src={displayImages[Math.min(selectedImage, displayImages.length - 1)]}
                        alt={`${product.name} - Imagem ${selectedImage + 1}`}
                        className="w-full h-full object-cover"
                      />
                      {displayImages.length > 1 && (
                        <>
                          <button
                            onClick={prevImage}
                            className="absolute left-4 top-1/2 -translate-y-1/2 bg-background/80 hover:bg-background p-2 rounded-full shadow-lg transition-colors"
                            aria-label="Imagem anterior"
                          >
                            <ChevronLeft className="h-6 w-6" />
                          </button>
                          <button
                            onClick={nextImage}
                            className="absolute right-4 top-1/2 -translate-y-1/2 bg-background/80 hover:bg-background p-2 rounded-full shadow-lg transition-colors"
                            aria-label="Próxima imagem"
                          >
                            <ChevronRight className="h-6 w-6" />
                          </button>
                        </>
                      )}
                    </div>

                    {displayImages.length > 1 && (
                      <div className="flex gap-2 overflow-x-auto pb-2">
                        {displayImages.map((img, idx) => (
                          <button
                            key={idx}
                            onClick={() => setSelectedImage(idx)}
                            className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                              selectedImage === idx
                                ? 'border-primary'
                                : 'border-transparent hover:border-muted-foreground/50'
                            }`}
                          >
                            <img
                              src={img}
                              alt={`Miniatura ${idx + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="aspect-square bg-muted flex items-center justify-center rounded-lg">
                    <p className="text-muted-foreground">
                      Sem imagem disponível
                    </p>
                  </div>
                )}
              </div>

              {product.features.length > 0 && (
                <div className="mt-6 space-y-3">
                  <h3 className="font-semibold text-lg">Características:</h3>
                  {product.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-secondary mt-0.5 flex-shrink-0" />
                      <span className="text-muted-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-6">
              <div>
                <span className="text-xs font-semibold text-foreground px-2 py-1 bg-muted rounded border border-border">
                  {product.level}
                </span>
                <h1 className="font-serif text-4xl md:text-5xl font-bold mt-3 tracking-tight uppercase">
                  {product.name}
                </h1>
                <p className="text-lg text-muted-foreground mt-2">
                  {product.description}
                </p>
              </div>

              <div className="text-3xl font-bold text-primary">
                {product.basePrice > 0
                  ? `A partir de R$ ${product.basePrice.toLocaleString(
                      'pt-BR',
                      {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      },
                    )}`
                  : 'Consulte'}
              </div>
              <ProductCustomizationInline
                product={product}
                onFormatChange={handleFormatChange}
              />
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}

export default ProductDetails
